import { Alert, Box, Chip, Divider, Paper, Typography } from '@mui/material'
import { Extension as ExtensionIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { EditorJsField } from '@/components/editor/EditorJsField'
import { homeworkItems } from '@/utils/homework'
import type { Homework } from '@/types'

interface Props {
  homework: Homework
  /** Показывать ответы ученика рядом с заданием */
  withAnswers?: boolean
}

/**
 * Домашка на чтение: блоки по порядку, у каждого задание и (по флагу) ответ.
 *
 * Домашка из одного текстового блока выглядит ровно как раньше — без
 * нумерации и лишних рамок.
 */
export function HomeworkItemsView({ homework, withAnswers = false }: Props) {
  const { t } = useTranslation()
  const items = homeworkItems(homework)
  const numbered = items.length > 1

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('homeworks.noItems', 'В домашнем задании нет блоков')}
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, index) => (
        <Box key={item.id}>
          {(numbered || item.completed_at) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {numbered && (
                <Typography variant="subtitle2">
                  {t('homeworks.itemNumber', 'Блок {{n}}', { n: index + 1 })}
                </Typography>
              )}
              {/* Отметка нужна и у домашки из одного блока: без неё работа
                  ученика, выполненная без текста, не видна учителю вовсе */}
              {item.completed_at && (
                <Chip size="small" color="success" label={t('homeworks.itemDone', 'выполнен')} />
              )}
              {/* Оценка стоит у блока, а не у домашки: проверяется работа */}
              {item.checked_at && typeof item.teacher_score_percent === 'number' && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={`${item.teacher_score_percent}%`}
                />
              )}
            </Box>
          )}

          {/* Блок-задание своего текста не имеет: сервер кладёт в task_json и
              task_text null, и без этой ветки модалка открывалась пустой */}
          {item.type === 'exercise' ? (
            <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionIcon color="action" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {item.exercise?.title ?? t('homeworks.exerciseItem', 'Интерактивное задание')}
                </Typography>
                {item.exercise && (
                  <Typography variant="caption" color="text.secondary">
                    {t(`exercises.types.${item.exercise.type}.name`)}
                  </Typography>
                )}
              </Box>
              {/* Статус показываем только необычный: «Готово» у каждой строки —
                  шум, а вот черновик или архив объясняют, почему ученик застрял */}
              {item.exercise && item.exercise.status !== 'ready' && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t(`exercises.exerciseStatus.${item.exercise.status}`)}
                />
              )}
            </Paper>
          ) : item.task_json ? (
            <EditorJsField
              key={`hw-task-${item.id}`}
              value={item.task_json}
              onChange={() => {}}
              readOnly
              // Без регистрации расширенных блоков Editor.js просто пропустит
              // таблицу или предупреждение: учитель их вставил, читатель не увидел
              extendedBlocks
              readOnlySurface="paper"
              hideLabel
              initDelayMs={50}
            />
          ) : item.task_text?.trim() ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {item.task_text}
            </Typography>
          ) : (
            // Заглушка вместо пустого Typography: следующий новый тип блока
            // иначе снова даст молча пустую модалку без единой подсказки
            <Typography variant="body2" color="text.secondary">
              {t('homeworks.itemNoTask', 'Этот блок нечем показать')}
            </Typography>
          )}

          {withAnswers && (item.answer_json || item.answer_text) && (
            <Box sx={{ mt: 1 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary">
                {t('homeworks.answer', 'Ответ')}
              </Typography>
              {item.answer_json ? (
                <EditorJsField
                  key={`hw-answer-${item.id}`}
                  value={item.answer_json}
                  onChange={() => {}}
                  readOnly
                  extendedBlocks
                  readOnlySurface="paper"
                  hideLabel
                  initDelayMs={50}
                />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {item.answer_text}
                </Typography>
              )}
            </Box>
          )}

          {/* Комментарий живёт у блока и переживает пересдачу: по нему ученик
              и правит ответ */}
          {item.teacher_comment && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
              {item.teacher_comment}
            </Alert>
          )}
        </Box>
      ))}
    </Box>
  )
}
